import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CartBuyerModel, CartBuyerModelConverter, ApiModel } from '@steelbuy/ts-shared';
import { toaster } from '@steelbuy/ui-primitive';
import axios from '../../axios';
import { getHideCartExpiryToast } from '../../utils/cart';

export const MY_CART_DETAILS = 'cart-details';

const modelConverter = new CartBuyerModelConverter();

const getCartDetails = async () => {
    const { data } = await axios.get<CartBuyerModel>(`/api/buyer/cart/fetch-cart`);
    const newData = modelConverter.toViewModel(data as unknown as ApiModel<CartBuyerModel>);
    return newData;
};

export const useCartDetails = (
    { queryOptions } = { queryOptions: { enabled: true } },
    showCartReminderToast?: (time: number) => void
) => {
    const { t } = useTranslation('translation');
    const expiresAtRef = useRef<number>(0);
    const { data, refetch, ...rest } = useQuery({
        queryKey: [MY_CART_DETAILS],
        queryFn: () => getCartDetails(),
        cacheTime: 0,
        ...queryOptions,
    });
    const thirtyMinuteToMSeconds = 30 * 60 * 1000;
    const tenMinuteToMSeconds = 10 * 60 * 1000;

    useEffect(() => {
        if (showCartReminderToast && data?.expiresAt?.value) {
            const cartExpirationTime = new Date(data?.expiresAt?.value).getTime();
            const currentTime = Date.now();
            if (expiresAtRef.current !== cartExpirationTime) {
                expiresAtRef.current = cartExpirationTime;
                const timeUntilExpiration = cartExpirationTime - currentTime;
                if (timeUntilExpiration > 0) {
                    const timeoutId = setTimeout(() => {
                        refetch();
                        if (!getHideCartExpiryToast()) {
                            toaster.warn(t('application.cart.expiredMessage'));
                        }
                    }, timeUntilExpiration);
                    let thirtyMinTimeout: NodeJS.Timeout;
                    let tenMinTimeout: NodeJS.Timeout;

                    if (timeUntilExpiration > thirtyMinuteToMSeconds) {
                        thirtyMinTimeout = setTimeout(() => {
                            showCartReminderToast(30);
                        }, timeUntilExpiration - thirtyMinuteToMSeconds);
                    }
                    if (timeUntilExpiration > tenMinuteToMSeconds) {
                        tenMinTimeout = setTimeout(() => {
                            showCartReminderToast(10);
                        }, timeUntilExpiration - tenMinuteToMSeconds);
                    }
                    return () => {
                        clearTimeout(thirtyMinTimeout);
                        clearTimeout(tenMinTimeout);
                        clearTimeout(timeoutId);
                    };
                }
            }
        }

        return undefined;
    }, [data?.expiresAt?.value]);

    return { data, refetch, ...rest };
};
